import {
  AppState,
  getAppLoading,
  getLoaded,
  IInstance,
  instance,
  IUser,
  loadUser,
  user,
} from '@vantage-platform/store';
import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  ViewEncapsulation,
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';

import { AuthService, LOCAL_STORAGE, SIGN_IN_ACTION } from "@vantage-platform/auth";
import { RoutingStateService } from "@vantage-platform/store/lib/services/routing-state.service";
import { AppResizeService, SideNavService } from '@vantage-platform/common-ui';
import { Store } from '@ngrx/store';
import { setTheme } from "ngx-bootstrap/utils";
import { HelpersService } from './shared/helpers.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [SideNavService],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'vantage-focus-web-app';

  isIframe = false;
  $isLoaded: Observable<boolean>;
  $isAppLoading: Observable<boolean>;
  $user: Observable<IUser>;
  $instance: Observable<IInstance>;

  private readonly supportedLanguages = ['en', 'fr'];
  private readonly languageStorageKey = 'preferredLanguageCode'; // legacy fallback
  private readonly languageIdStorageKey = 'preferredLanguageId';
  private readonly languageCodeById: Record<number, string> = {
    1: 'en',
    3: 'fr',
  };
  private languageSubscription: Subscription;


  @HostListener('document:visibilitychange', ['$event'])
  appVisibility() {
    if (!document.hidden) {
      if (!this.auth.user && !!this.localStorage?.getItem(SIGN_IN_ACTION)) {
        this.router.navigate(['/login']);
      } else if (this.auth.checkIfSessionExpired()) {
        this.auth.logOut(window.location.origin + this.router.url);
      }
    }
  }

  constructor(
    public auth: AuthService,
    private store: Store<AppState>,
    private routingStateService: RoutingStateService,
    private router: Router,
    private helpersService: HelpersService,
    public appResize: AppResizeService,
    @Inject(LOCAL_STORAGE) @Optional() private localStorage: Storage,
    private translate: TranslateService
  ) {
    if (!this.localStorage) {
      this.localStorage = window.localStorage;
    }

    setTheme('bs4');
    this.$isLoaded = this.store.select(getLoaded);
    this.$isAppLoading = this.store.select(getAppLoading);
    this.$user = this.store.select(user);
    this.$instance = this.store.select(instance);

    this.isIframe = window !== window.parent && !window.opener;
    this.routingStateService.loadRouting();
    this.translate.addLangs(this.supportedLanguages);
    const initialLanguage = this.resolveInitialLanguageCode();
    this.translate.setDefaultLang(initialLanguage);
    this.translate.use(initialLanguage);

    this.languageSubscription = this.$user
      .pipe(
        map((currentUser) => {
          const hasLoadedUserLanguage = currentUser?.id && typeof currentUser.defaultLanguageID === 'number';
          const language = hasLoadedUserLanguage
            ? this.resolveLanguageCode(currentUser.defaultLanguageCode)
            : this.resolveInitialLanguageCode();

          if (hasLoadedUserLanguage) {
            this.localStorage?.setItem(
              this.languageIdStorageKey,
              String(currentUser.defaultLanguageID)
            );
          }

          return language;
        }),
        distinctUntilChanged()
      )
      .subscribe((language) => this.translate.use(language));
  }

  ngOnInit() {
    if (this.auth.isLoggedIn) {
      this.store
        .pipe(
          filter((s) => !!s.router),
          take(1)
        )
        .subscribe((s) => {
          console.log(s);
          this.helpersService.initPageTitle();
          this.store.dispatch(loadUser());
        });
    }
  }

  ngOnDestroy(): void {
    this.languageSubscription?.unsubscribe();
  }

  private resolveLanguageCode(languageCode?: string): string {
    const normalize = (code?: string) =>
      code ? code.split('-')[0].toLowerCase() : undefined;

    const userPreferred = normalize(languageCode);
    if (userPreferred && this.supportedLanguages.includes(userPreferred)) {
      return userPreferred;
    }

    const browserLang = normalize(this.translate.getBrowserLang());
    if (browserLang && this.supportedLanguages.includes(browserLang)) {
      return browserLang;
    }

    return 'en';
  }

  private resolveInitialLanguageCode(): string {
    const cachedLanguageId = Number(this.localStorage?.getItem(this.languageIdStorageKey));
    if (!isNaN(cachedLanguageId)) {
      const languageCodeById = this.languageCodeById[cachedLanguageId];
      if (languageCodeById && this.supportedLanguages.includes(languageCodeById)) {
        return languageCodeById;
      }
    }

    const legacyCachedLanguageCode = this.localStorage?.getItem(this.languageStorageKey);
    return this.resolveLanguageCode(legacyCachedLanguageCode);
  }
}
